import { useEffect, useRef } from "react";
import { BreakoutEngine } from "./engine";

/**
 * Login sol kolonunda, ızgaranın üstünde duran breakout tuvali.
 * Marka ve dil seçici bu katmanın üstünde kaldığı için tıklamayı onlar alır.
 */
export const AuthBreakout = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const engine = new BreakoutEngine();
    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    engine.setReducedMotion(reducedQuery.matches);

    const syncSize = () => {
      const rect = parent.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      engine.resize(width, height);
    };

    syncSize();
    const resizeObserver = new ResizeObserver(syncSize);
    resizeObserver.observe(parent);

    const onReduce = () => engine.setReducedMotion(reducedQuery.matches);
    reducedQuery.addEventListener("change", onReduce);

    const localX = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return event.clientX - rect.left;
    };

    const onMove = (event: PointerEvent) => {
      engine.setPointer(localX(event));
    };
    const onLeave = () => engine.setPointer(null);
    const onDown = (event: PointerEvent) => {
      engine.setPointer(localX(event));
      engine.launch();
    };

    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointerleave", onLeave);

    let frame = 0;
    let last = performance.now();
    const loop = (now: number) => {
      if (!document.hidden) {
        const dt = Math.min(0.034, (now - last) / 1000);
        engine.setDark(document.documentElement.classList.contains("dark"));
        engine.step(dt);
        engine.draw(ctx, now / 1000);
      }
      last = now;
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      reducedQuery.removeEventListener("change", onReduce);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div className="pointer-events-auto relative h-full min-h-0 w-full">
      <canvas
        ref={canvasRef}
        aria-hidden
        className="absolute inset-0 h-full w-full cursor-crosshair touch-none"
      />
    </div>
  );
};
